import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { FIELDS as FF } from "@/lib/fields";
import { CalendarClock, MessageSquareText } from "lucide-react";
import { MessageDialog } from "@/components/SuggestedMessages";
import { toast } from "sonner";
import { FollowupPicker } from "@/components/FollowupPicker";
import { FromTheCall } from "@/components/AiSuggestions";

const btn = (primary) =>
  `text-xs rounded-lg px-2.5 py-1.5 font-medium whitespace-nowrap transition-colors ${
    primary
      ? "bg-[#FF4802] text-white hover:bg-[#e03e00]"
      : "border border-[#E2E2E2] text-[#6B7280] hover:border-[#FF4802]"
  }`;

const fmtTime = (iso) => {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "numeric", minute: "2-digit" }); }
  catch { return ""; }
};

// Shared post-consultation guided check-in. Used by My Day card + case Summary banner.
export function CheckinCard({ cs, selects: selectsProp, onDone, embedded }) {
  const { user } = useAuth();
  const rid = cs.record_id;
  const who = cs.consult_name || cs.name || "the customer";
  const first = who.split(" ")[0];
  const [step, setStep] = useState("outcome"); // outcome | moving | thinking | notint | group | done
  const [selects, setSelects] = useState(selectsProp || null);
  const [busy, setBusy] = useState(false);
  const [sugg, setSugg] = useState(null);
  const [hlRm, setHlRm] = useState(cs.consultation_taken_by || "");
  const [conf, setConf] = useState("");
  const [quarter, setQuarter] = useState("");
  const plus = (d) => { const x = new Date(); x.setDate(x.getDate() + d); return x.toISOString().slice(0, 10); };
  const [fuDate, setFuDate] = useState("");
  const [nextAction, setNextAction] = useState("");
  const [prefill, setPrefill] = useState({});
  const [lostReason, setLostReason] = useState("");
  // outcome templates
  const [allTmpls, setAllTmpls] = useState([]);
  const [waLink, setWaLink] = useState(null);
  const [tmplNames, setTmplNames] = useState([]);
  const [openTmpl, setOpenTmpl] = useState(null);

  useEffect(() => {
    if (!selects) api.get("/meta/selects").then((r) => setSelects(r.data.selects || {})).catch(() => setSelects({}));
    api.get("/message-templates").then((r) => setAllTmpls(r.data.templates || [])).catch(() => setAllTmpls([]));
    api.get(`/cases/${rid}/whatsapp`).then((r) => setWaLink(r.data.customer_open_link || null)).catch(() => {});
  }, []); // eslint-disable-line

  const opts = (fid, fallback = []) => (selects && selects[fid]) || fallback;
  const fillBody = (body) => (body || "").replace(/\{(name|rm_name)\}/g, (m, k) =>
    k === "name" ? first : ((user?.name || "").split(" ")[0] || m));
  const tmplByName = (name) => allTmpls.find((t) => t.name === name);

  const skip = async () => {
    try { await api.post(`/cases/${rid}/checkin/skip`); toast.success("Hidden"); onDone(); }
    catch { toast.error("Failed"); }
  };
  const snooze = async () => {
    try { await api.post(`/cases/${rid}/checkin/snooze`); toast.success("Will remind you in 1 hour"); onDone(); }
    catch { toast.error("Failed"); }
  };

  const loadGroups = async () => {
    setStep("group");
    if (sugg === null) {
      try { const r = await api.get("/whatsapp/suggestions", { params: { case_record_id: rid } }); setSugg(r.data.suggestions || []); }
      catch { setSugg([]); }
    }
  };

  const moveAhead = async () => {
    if (!hlRm || !conf || !quarter) { toast.error("HL RM, Confidence and Quarter are required"); return; }
    setBusy(true);
    try {
      await api.post(`/cases/${rid}/stage`, { stage: "Document Collection", hl_rm: hlRm, rm_confidence: conf, expected_quarter: quarter, prefill });
      toast.success("Moved to Document Collection");
      setTmplNames(["Post consultation", "Second request for documents"]);
      await loadGroups();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not update stage"); }
    finally { setBusy(false); }
  };

  const setThinking = async () => {
    if (!fuDate) { toast.error("Pick a follow-up date"); return; }
    if (!nextAction.trim()) { toast.error("Next action is required"); return; }
    setBusy(true);
    try {
      await api.post(`/cases/${rid}/followup`, { follow_up_date: fuDate, next_action: nextAction.trim(), outcome: "thinking" });
      toast.success("Follow-up set");
      setTmplNames(["Follow-up nudge"]);
      await loadGroups();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not set follow-up"); }
    finally { setBusy(false); }
  };

  const setLost = async () => {
    if (!lostReason) { toast.error("Pick a reason"); return; }
    setBusy(true);
    try {
      await api.post(`/cases/${rid}/stage`, { stage: "Lost", lost_reason: lostReason });
      await api.post(`/cases/${rid}/checkin/skip`);
      toast.success("Marked Lost");
      setTmplNames([]);
      setStep("done");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not mark Lost"); }
    finally { setBusy(false); }
  };

  const setNoShow = async () => {
    setBusy(true);
    try {
      await api.patch(`/cases/${rid}`, { fields: { [FF.CONSULTATION_STATUS]: "Didn't Show Up" } });
      toast.success("Marked as didn't show up");
      setTmplNames(["No-show"]);
      setStep("done");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not update"); }
    finally { setBusy(false); }
  };

  const link = async (chat_id, chat_type) => {
    setBusy(true);
    try {
      const r = await api.post(`/cases/${rid}/whatsapp/link`, { chat_id, chat_type });
      toast.success(`Linked${r.data.importing ? " · importing history in the background" : ""}`);
      onDone();
    } catch { toast.error("Link failed"); }
    finally { setBusy(false); }
  };

  const Sel = ({ value, onChange, options, placeholder, testid }) => (
    <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
      className="text-xs border border-[#E2E2E2] rounded-lg px-2 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-[#FF4802]/40">
      <option value="">{placeholder}</option>
      {options.map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
  );

  const TemplateChips = () => {
    const chips = tmplNames.map(tmplByName).filter(Boolean);
    if (!chips.length) return null;
    return (
      <div className="mt-2 pt-2 border-t border-[#8B46FF]/15" data-testid={`checkin-templates-${cs.case_id}`}>
        <div className="text-[11px] text-[#6B7280] font-medium flex items-center gap-1 mb-1.5">
          <MessageSquareText className="w-3.5 h-3.5 text-[#FF4802]" /> Suggested message
        </div>
        <div className="flex flex-wrap gap-1.5">
          {chips.map((t) => (
            <button key={t.id} onClick={() => setOpenTmpl(t)} data-testid={`checkin-tmpl-${t.id}`}
              className="px-3 py-1.5 rounded-full text-xs border border-[#E2E2E2] bg-white text-[#333333] hover:border-[#FF6F38] hover:bg-[#FF4802]/5 transition-colors">{t.name}</button>
          ))}
        </div>
      </div>
    );
  };

  const missing = cs.consult?.outcome_missing;
  const shell = (embedded
    ? "rounded-2xl border p-4 "
    : "rounded-2xl border p-3 ") + (missing ? "border-[#CA1212]/50 bg-[#CA1212]/5" : "border-[#8B46FF]/25 bg-[#8B46FF]/5");

  return (
    <div className={shell} data-testid={`checkin-card-${cs.case_id}`} data-outcome-missing={missing ? "1" : "0"}>
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          {embedded && <CalendarClock className="w-4 h-4 text-[#8B46FF] flex-shrink-0" />}
          <span className="font-mono text-[#E2521B] text-xs font-semibold">{cs.case_id}</span>
          <span className="text-sm text-[#333333] font-medium truncate">How did the call with {who} go?</span>
          {missing && <span className="text-[11px] font-semibold text-[#CA1212] bg-white border border-[#CA1212]/40 rounded-full px-2 py-0.5 whitespace-nowrap" data-testid={`checkin-outcome-missing-${cs.case_id}`}>Outcome missing · {cs.consult.outcome_days} day{cs.consult.outcome_days === 1 ? "" : "s"}</span>}
        </div>
        <button onClick={skip} data-testid={`checkin-skip-${cs.case_id}`} className="text-[11px] text-[#6B7280] hover:underline flex-shrink-0">Skip</button>
      </div>

      {step === "outcome" && (
        <div className="flex flex-wrap gap-1.5 mt-2.5">
          <button onClick={() => setStep("moving")} data-testid={`checkin-moving-${cs.case_id}`} className={btn(true)}>Moving ahead</button>
          <button onClick={() => setStep("thinking")} data-testid={`checkin-thinking-${cs.case_id}`} className={btn(false)}>Thinking</button>
          <button onClick={() => setStep("notint")} data-testid={`checkin-notint-${cs.case_id}`} className={btn(false)}>Not interested</button>
          <button onClick={setNoShow} disabled={busy} data-testid={`checkin-noshow-${cs.case_id}`} className={btn(false)}>Didn't happen</button>
          <button onClick={loadGroups} data-testid={`checkin-group-${cs.case_id}`} className="text-xs rounded-lg px-2.5 py-1.5 font-medium border border-[#159A50]/40 text-[#159A50] hover:bg-[#159A50]/5">Which group is {first}'s?</button>
        </div>
      )}

      {step === "moving" && (
        <div className="mt-2.5 flex flex-wrap items-center gap-2" data-testid={`checkin-moving-form-${cs.case_id}`}>
          <span className="text-[11px] text-[#6B7280]">Move to Document Collection:</span>
          <Sel value={hlRm} onChange={setHlRm} options={opts(FF.CONSULTATION_TAKEN_BY, ["Afif", "Arpit", "Arhan", "Harsh", "Ollie", "Sana", "Aditya"])} placeholder="HL RM" testid={`checkin-hlrm-${cs.case_id}`} />
          <Sel value={conf} onChange={setConf} options={opts(FF.RM_CONFIDENCE, ["25%", "50%", "75%", "90%"])} placeholder="Confidence" testid={`checkin-conf-${cs.case_id}`} />
          <Sel value={quarter} onChange={setQuarter} options={opts(FF.EXPECTED_DISB_QUARTER)} placeholder="Quarter" testid={`checkin-quarter-${cs.case_id}`} />
          <button onClick={moveAhead} disabled={busy} data-testid={`checkin-moving-confirm-${cs.case_id}`} className={btn(true)}>Confirm</button>
          <button onClick={() => setStep("outcome")} className="text-[11px] text-[#6B7280] hover:underline">Back</button>
          {Object.keys(prefill).length > 0 && <span className="text-[10px] text-[#8B46FF] w-full" data-testid={`checkin-prefill-note-${cs.case_id}`}>Pre-filled from the call: {Object.keys(prefill).map((k) => k.replace(/_/g, " ")).join(", ")} — you only add Confidence + Quarter.</span>}
        </div>
      )}
      {(step === "outcome" || step === "moving") && <FromTheCall recordId={rid} onChange={setPrefill} />}

      {step === "thinking" && (
        <div className="mt-2.5 flex flex-wrap items-center gap-2" data-testid={`checkin-thinking-form-${cs.case_id}`}>
          <span className="text-[11px] text-[#6B7280]">Follow up on:</span>
          <FollowupPicker value={fuDate} onChange={setFuDate} idPrefix={`checkin-fu-${cs.case_id}`} withNextAction nextAction={nextAction} onNextAction={setNextAction} />
          <button onClick={setThinking} disabled={busy} data-testid={`checkin-thinking-confirm-${cs.case_id}`} className={btn(true)}>Confirm</button>
          <button onClick={() => setStep("outcome")} className="text-[11px] text-[#6B7280] hover:underline">Back</button>
        </div>
      )}

      {step === "notint" && (
        <div className="mt-2.5 flex flex-wrap items-center gap-2" data-testid={`checkin-notint-form-${cs.case_id}`}>
          <span className="text-[11px] text-[#6B7280]">Mark Lost — reason:</span>
          <Sel value={lostReason} onChange={setLostReason} options={opts(FF.LOST_REASON)} placeholder="Lost reason" testid={`checkin-lostreason-${cs.case_id}`} />
          <button onClick={setLost} disabled={busy} data-testid={`checkin-notint-confirm-${cs.case_id}`} className={btn(true)}>Confirm</button>
          <button onClick={() => setStep("outcome")} className="text-[11px] text-[#6B7280] hover:underline">Back</button>
        </div>
      )}

      {step === "group" && (
        <div className="mt-2.5 space-y-1.5" data-testid={`checkin-groups-${cs.case_id}`}>
          <div className="text-[11px] text-[#6B7280] font-medium">Which WhatsApp group is {who}'s?</div>
          {sugg === null && <div className="text-[11px] text-[#C5C4C4]">Finding chats…</div>}
          {sugg && sugg.length === 0 && <div className="text-[11px] text-[#6B7280]">No matching chats found.</div>}
          {(sugg || []).map((s) => (
            <div key={s.chat_id} className="flex items-center justify-between gap-3 text-xs bg-white border border-[#E2E2E2] rounded-lg px-3 py-1.5">
              <div className="min-w-0">
                <div className="truncate text-[#333333] font-medium">{s.name || s.chat_id}</div>
                <div className="text-[11px] text-[#C5C4C4] truncate">{(s.reasons || []).join(" · ")}</div>
                {s.preview && (
                  <div className="text-[11px] text-[#9A9A9A] truncate mt-0.5" data-testid={`checkin-preview-${s.chat_id}`}>
                    <span className="font-medium text-[#6B7280]">{s.preview.sender}:</span> {s.preview.text}
                    {s.preview.when ? <span className="text-[#C5C4C4]"> · {fmtTime(s.preview.when)}</span> : null}
                  </div>
                )}
              </div>
              <div className="flex gap-1.5 flex-shrink-0">
                <button onClick={() => link(s.chat_id, "customer")} disabled={busy} data-testid={`checkin-link-${s.chat_id}`} className={btn(s.likely_type === "customer")}>Link as customer group</button>
                <button onClick={() => link(s.chat_id, "partner")} disabled={busy} className={btn(s.likely_type === "partner")}>Link as partner group</button>
              </div>
            </div>
          ))}
          <div className="flex flex-wrap gap-3 text-[11px] pt-0.5">
            <button onClick={snooze} className="text-[#6B7280] hover:underline" data-testid={`checkin-remind-${cs.case_id}`}>Not created yet — remind me in 1 hour</button>
            <button onClick={async () => { try { await api.post(`/cases/${rid}/whatsapp/prefers-1to1`, { value: true }); } catch { /* noop */ } skip(); }} className="text-[#6B7280] hover:underline" data-testid={`checkin-1on1-${cs.case_id}`}>Customer prefers 1:1</button>
          </div>
          <TemplateChips />
        </div>
      )}

      {step === "done" && (
        <div className="mt-2.5" data-testid={`checkin-done-${cs.case_id}`}>
          <TemplateChips />
          <button onClick={onDone} data-testid={`checkin-done-btn-${cs.case_id}`} className="mt-2 text-[11px] text-[#6B7280] hover:underline">Done</button>
        </div>
      )}

      {openTmpl && (
        <MessageDialog tmpl={openTmpl} initial={fillBody(openTmpl.body)} recordId={rid}
          name={first} waOpenLink={waLink} onClose={() => setOpenTmpl(null)} />
      )}
    </div>
  );
}
